/**
 * Formatting for the timestamps the UI shows.
 *
 * RainViewer works in Unix seconds; everything the user sees is in their own
 * timezone, because "is it raining on me in 20 minutes" is a local question.
 */

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: '2-digit',
});

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  hour: 'numeric',
  minute: '2-digit',
});

/** `3:40 PM` — the frame's valid time in the viewer's timezone. */
export function formatFrameTime(unixSeconds: number): string {
  return timeFormatter.format(unixSeconds * 1000);
}

/** `Thu 3:40 PM` — used in tooltips where the day could be ambiguous. */
export function formatFrameDateTime(unixSeconds: number): string {
  return dateTimeFormatter.format(unixSeconds * 1000);
}

const searchTimeFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

/**
 * `14 Aug 2026, 3:40 PM` — when a search happened. The full date is spelled out
 * because history reaches back months, where a bare clock time says nothing.
 * Unlike radar frames this one is already in epoch milliseconds.
 */
export function formatSearchTime(epochMilliseconds: number): string {
  return searchTimeFormatter.format(epochMilliseconds);
}

const calendarDateFormatter = new Intl.DateTimeFormat(undefined, { dateStyle: 'long' });

/**
 * `14 August 2026` — a plain calendar day, from an ISO `YYYY-MM-DD`.
 *
 * Built from the parts rather than `new Date(iso)`, which reads a bare date as
 * UTC midnight and so renders as the day before anywhere west of Greenwich.
 * Returns the input unchanged if it isn't a date, since the caller is showing
 * this to a reader either way.
 */
export function formatCalendarDate(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return isoDate;

  const [year, month, day] = match.slice(1).map(Number) as [number, number, number];
  return calendarDateFormatter.format(new Date(year, month - 1, day));
}

/**
 * `now`, `40 min ago`, `in 20 min` — the offset from the feed's generation
 * time, which is what tells a viewer whether they're looking at observation or
 * forecast.
 */
export function formatFrameOffset(unixSeconds: number, referenceSeconds: number): string {
  const minutes = Math.round((unixSeconds - referenceSeconds) / 60);

  if (Math.abs(minutes) < 5) return 'now';
  if (minutes < 0) return `${Math.abs(minutes)} min ago`;
  return `in ${minutes} min`;
}
